import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import { Navigation, Plus, Edit3, Trash2, X, Search, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getAllRoutes,
  createNewRoute,
  editRoute,
  deleteRoute,
  getAllZones,
  getAllLocations,
  getAllHeadquarters,
  RoutePaginationMeta,
  RouteServerFilters,
} from "../../api/master";
import { useAuth } from "../../context/AuthContext";

interface Zone {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
  head_quarter_id?: number;
}

interface HeadQuarter {
  id: number;
  name: string;
}

interface Route {
  id: number;
  name: string;
  zone_id: number;
  from_location_id: number;
  to_location_id: number;
  mode: string;
  distance: number;
  cities_in_route?: string;
  zone?: Zone;
  from_location?: Location;
  to_location?: Location;
  updated_at?: string;
}

const TRAVEL_MODES = ["Bike", "Train", "Bus"];

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const PER_PAGE = 15;

const RouteMaster: React.FC = () => {
  const { user } = useAuth();
  const [routes, setRoutes] = useState<Route[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [headQuarters, setHeadQuarters] = useState<HeadQuarter[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentRouteId, setCurrentRouteId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<RoutePaginationMeta>({ current_page: 1, last_page: 1, per_page: PER_PAGE, total: 0 });
  const [search, setSearch] = useState("");
  const [hqFilter, setHqFilter] = useState("");
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadRoutes = useCallback(async (page: number = 1, filters: RouteServerFilters = {}) => {
    if (!user?.tenant_id) return;
    setIsLoading(true);
    try {
      const result = await getAllRoutes(user.tenant_id, page, PER_PAGE, filters);
      setRoutes(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Fetch error:", error);
    }
    setIsLoading(false);
  }, [user?.tenant_id]);

  const serverFilters = (): RouteServerFilters => ({
    search: search || undefined,
    head_quarter_id: hqFilter ? Number(hqFilter) : undefined,
  });

  useEffect(() => {
    if (user?.tenant_id) {
      loadRoutes(1, serverFilters());
      fetchDropdownData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchDropdownData = async () => {
    try {
      const [zoneData, locationData, hqData] = await Promise.all([
        getAllZones(user.tenant_id),
        getAllLocations(user.tenant_id),
        getAllHeadquarters(user.tenant_id),
      ]);
      setZones(zoneData);
      setLocations(locationData);
      setHeadQuarters(hqData);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      loadRoutes(1, { search: value || undefined, head_quarter_id: hqFilter ? Number(hqFilter) : undefined });
    }, 400);
  };

  const handleHqFilterChange = (value: string) => {
    setHqFilter(value);
    loadRoutes(1, { search: search || undefined, head_quarter_id: value ? Number(value) : undefined });
  };

  const clearFilters = () => {
    setSearch("");
    setHqFilter("");
    loadRoutes(1, {});
  };

  const hasActiveFilters = !!(search || hqFilter);

  const zoneOptions = zones.map((z) => ({ label: z.name, value: z.id }));
  const locationOptions = locations.map((l) => ({ label: l.name, value: l.id }));

  const routeSchema = Yup.object({
    zone_id: Yup.number().min(1, "Please select a Zone").required("Zone is required"),
    from_location_id: Yup.number().min(1, "Please select From Location").required("From Location is required"),
    to_location_id: Yup.number().min(1, "Please select To Location").required("To Location is required"),
    mode: Yup.string().oneOf(TRAVEL_MODES, "Invalid mode").required("Mode is required"),
    distance: Yup.number().min(0, "Distance must be 0 or more").required("Distance is required"),
    cities_in_route: Yup.string().max(255),
  });

  const initialValues = {
    zone_id: 0,
    from_location_id: 0,
    to_location_id: 0,
    mode: "",
    distance: 0,
    cities_in_route: "",
  };

  const {
    setValues,
    setFieldValue,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: routeSchema,
    onSubmit: async (vals) => {
      try {
        if (!isEditModalOpen) {
          const response = await createNewRoute(
            user.tenant_id,
            vals.zone_id,
            vals.from_location_id,
            vals.to_location_id,
            vals.mode,
            vals.distance,
            vals.cities_in_route
          );
          if (response.status === 200) {
            setIsAddModalOpen(false);
            resetForm();
            await loadRoutes(pagination.current_page, serverFilters());
            toast.success("Route Created Successfully!");
          }
        } else {
          const response = await editRoute(
            currentRouteId,
            user.tenant_id,
            vals.zone_id,
            vals.from_location_id,
            vals.to_location_id,
            vals.mode,
            vals.distance,
            vals.cities_in_route
          );
          if (response.status === 200) {
            setIsEditModalOpen(false);
            resetForm();
            await loadRoutes(pagination.current_page, serverFilters());
            toast.success("Route Updated Successfully!");
          }
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong.");
      }
    },
  });

  const handleEdit = (item: Route) => {
    setValues({
      zone_id: item.zone_id,
      from_location_id: item.from_location_id,
      to_location_id: item.to_location_id,
      mode: item.mode,
      distance: item.distance,
      cities_in_route: item.cities_in_route ?? "",
    });
    setCurrentRouteId(item.id);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete route?", "Delete")) return;
    try {
      await deleteRoute(id);
      await loadRoutes(pagination.current_page, serverFilters());
      toast.success("Route deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete route.");
    }
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    resetForm();
  };

  // Render as a plain function (not a component) to avoid remounting on every render
  const renderRouteForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Zone *</label>
        <Select
          name="zone_id"
          options={zoneOptions}
          value={zoneOptions.find((o) => o.value === values.zone_id) || null}
          onChange={(option) => setFieldValue("zone_id", option ? option.value : 0)}
          onBlur={handleBlur}
          isClearable
          isSearchable
          placeholder="-- Select Zone --"
          className="text-sm"
        />
        {errors.zone_id && touched.zone_id && <p className="text-red-500 text-sm mt-1">{errors.zone_id}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">From Location *</label>
        <Select
          name="from_location_id"
          options={locationOptions}
          value={locationOptions.find((o) => o.value === values.from_location_id) || null}
          onChange={(option) => setFieldValue("from_location_id", option ? option.value : 0)}
          onBlur={handleBlur}
          isClearable
          isSearchable
          placeholder="-- Select From Location --"
          className="text-sm"
        />
        {errors.from_location_id && touched.from_location_id && (
          <p className="text-red-500 text-sm mt-1">{errors.from_location_id}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">To Location *</label>
        <Select
          name="to_location_id"
          options={locationOptions}
          value={locationOptions.find((o) => o.value === values.to_location_id) || null}
          onChange={(option) => setFieldValue("to_location_id", option ? option.value : 0)}
          onBlur={handleBlur}
          isClearable
          isSearchable
          placeholder="-- Select To Location --"
          className="text-sm"
        />
        {errors.to_location_id && touched.to_location_id && (
          <p className="text-red-500 text-sm mt-1">{errors.to_location_id}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Mode *</label>
        <select
          name="mode"
          value={values.mode}
          onBlur={handleBlur}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">-- Select Mode --</option>
          {TRAVEL_MODES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        {errors.mode && touched.mode && <p className="text-red-500 text-sm mt-1">{errors.mode}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km) *</label>
        <input
          type="number"
          name="distance"
          value={values.distance}
          onBlur={handleBlur}
          onChange={handleChange}
          min={0}
          step="0.01"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {errors.distance && touched.distance && <p className="text-red-500 text-sm mt-1">{errors.distance}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cities in Route</label>
        <input
          type="text"
          name="cities_in_route"
          value={values.cities_in_route}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder="e.g. City A, City B, City C"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {errors.cities_in_route && touched.cities_in_route && (
          <p className="text-red-500 text-sm mt-1">{errors.cities_in_route}</p>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={closeModal}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {isEditModalOpen ? "Update Route" : "Add Route"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Route Master</h1>
            <p className="text-blue-600 mt-1">Manage Routes</p>
          </div>
          <button
            onClick={() => { resetForm(); setIsAddModalOpen(true); }}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Route
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6">
            <div className="flex flex-col justify-between gap-3 mb-6 md:flex-row md:items-center">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 shrink-0">
                <Navigation className="w-5 h-5 text-blue-600" />
                Routes
                <span className="text-sm font-normal text-gray-500">
                  ({pagination.total})
                </span>
              </h3>

              <div className="flex flex-col w-full gap-2 sm:flex-row md:w-auto">
                {/* Search */}
                <div className="relative flex-1 sm:w-56">
                  <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2" />
                  <input
                    type="text"
                    placeholder="Search routes..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full py-2 pr-8 text-sm border border-gray-300 rounded-lg pl-9 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {search && (
                    <button onClick={() => handleSearchChange("")} className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* HQ Filter */}
                <select
                  value={hqFilter}
                  onChange={(e) => handleHqFilterChange(e.target.value)}
                  className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:w-48"
                >
                  <option value="">All Headquarters</option>
                  {headQuarters.map((hq) => (
                    <option key={hq.id} value={String(hq.id)}>{hq.name}</option>
                  ))}
                </select>

                {/* Clear */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">SN</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Route</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Zone</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Mode</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Distance</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Cities</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Updated At</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-sm text-center text-gray-400">Loading routes...</td>
                    </tr>
                  ) : routes.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-sm text-center text-gray-400">
                        {hasActiveFilters ? "No routes match the current filters." : "No routes found."}
                      </td>
                    </tr>
                  ) : routes.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-4 font-medium text-gray-900">{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                      <td className="py-4 px-4 text-gray-900">
                        {item.from_location?.name ?? "-"} to {item.to_location?.name ?? "-"}
                      </td>
                      <td className="py-4 px-4 text-gray-900">{item.zone?.name ?? "-"}</td>
                      <td className="py-4 px-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
                          {item.mode}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-900">{item.distance} km</td>
                      <td className="py-4 px-4 text-gray-500 text-sm">{item.cities_in_route ?? "-"}</td>
                      <td className="py-4 px-4 text-gray-900">
                        {item.updated_at ? fmtDate(item.updated_at) : "-"}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                {pagination.total > 0 ? (
                  <>
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                    <span className="font-medium">{pagination.total}</span> routes
                    {hasActiveFilters && <span className="ml-1 text-blue-600">(filtered)</span>}
                  </>
                ) : (
                  <>0 routes</>
                )}
              </p>

              {pagination.last_page > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => loadRoutes(pagination.current_page - 1, serverFilters())}
                    disabled={pagination.current_page === 1}
                    className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === pagination.last_page || Math.abs(p - pagination.current_page) <= 1)
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                          <span className="px-1 text-gray-400 text-sm">…</span>
                        )}
                        <button
                          onClick={() => loadRoutes(p, serverFilters())}
                          className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg ${
                            p === pagination.current_page
                              ? 'bg-blue-600 text-white font-medium'
                              : 'border border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    onClick={() => loadRoutes(pagination.current_page + 1, serverFilters())}
                    disabled={pagination.current_page === pagination.last_page}
                    className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal isOpen={isAddModalOpen} onClose={closeModal} title="Add New Route">
          {renderRouteForm()}
        </Modal>

        <Modal isOpen={isEditModalOpen} onClose={closeModal} title="Edit Route">
          {renderRouteForm()}
        </Modal>
      </div>
    </div>
  );
};

export default RouteMaster;
